/**
 * Value Gap Card
 * Displays value gap with current and best-in-class values
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ChartColumnIncreasing, Info } from 'lucide-react';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';

const ValueGapCard = ({ valueGap, setShowValueGapDetails, handleRecalculateValueGap, isViewer = false }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 text-white">
        <div className="flex items-center space-x-3">
          <ChartColumnIncreasing size={24} />
          <div>
            <h3 className="text-lg font-bold">Value Gap</h3>
            <p className="text-xs text-emerald-100">Business Attractiveness and Readiness Impact</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        {valueGap ? (
          <div className="space-y-3">
            {/* Current State */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Current Est. Business Exit Value:</span>
                <span className="font-bold text-gray-900">{formatCurrency(valueGap.current_exit_value)}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-600">Current Overall Business Gap Score:</span>
                <span className="font-semibold text-amber-600">{valueGap.current_attractiveness?.toFixed(1)}%</span>
              </div>
            </div>

            {/* Green Zone Scenario */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs font-semibold text-blue-900 mb-2">If at Green Zone Score ({valueGap.green_zone_attractiveness?.toFixed(1)}%)</p>
              <div className="flex justify-between items-center">
                <span className="text-xs text-blue-700">Valuation Impact:</span>
                <span className="font-bold text-blue-600 text-lg">{formatCurrency(valueGap.value_impact_green_zone)}</span>
              </div>
            </div>

            {/* Best in Class Scenario */}
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs font-semibold text-green-900 mb-2">If at Best in Class Score ({valueGap.best_in_class_attractiveness?.toFixed(1)}%)</p>
              <div className="flex justify-between items-center">
                <span className="text-xs text-green-700">Valuation Impact:</span>
                <span className="font-bold text-green-600 text-lg">{formatCurrency(valueGap.value_impact_best_in_class)}</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <button
                onClick={() => setShowValueGapDetails(true)}
                className="flex-1 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 font-semibold transition text-sm flex items-center justify-center gap-1 min-h-[44px]"
              >
                <Info size={16} />
                Details
              </button>
              {!isViewer && (
                <button
                  onClick={handleRecalculateValueGap}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition text-sm min-h-[44px]"
                >
                  Recalculate
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">Complete valuation & assessment</p>
            {!isViewer && (
              <button
                onClick={handleRecalculateValueGap}
                className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition text-sm min-h-[44px]"
              >
                Calculate
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ValueGapCard.propTypes = {
  valueGap: PropTypes.shape({
    current_attractiveness: PropTypes.number,
    green_zone_attractiveness: PropTypes.number,
    best_in_class_attractiveness: PropTypes.number,
    current_exit_value: PropTypes.number,
    green_zone_value: PropTypes.number,
    best_in_class_value: PropTypes.number,
    value_gap: PropTypes.number,
    value_impact_green_zone: PropTypes.number,
    value_impact_best_in_class: PropTypes.number
  }),
  setShowValueGapDetails: PropTypes.func.isRequired,
  handleRecalculateValueGap: PropTypes.func.isRequired,
  isViewer: PropTypes.bool
};

export default ValueGapCard;
